<template>
    <modal
        v-if="billing"
        :title="$t('OrderCurrent.actions.billing')"
        @close-request="clearSelected"
        closable
      >
        <table-element
          :isShowFilter="true"
          :title="$t('OrderCurrent.modal.selected_orders')"
          :fields="billingFieldsObj"
          :arrOfElement="billingRows"
          :sortSpacing="0"
          class="card mx-2 mt-4"
        >
          <template #header-actions>
            <p class="m-2 mb-0 mt-2">{{$t('OrderCurrent.count')}} {{billingRows.length}}</p>
            <div v-if="isOdooBill" class="billing-summary mx-2 mt-2">
              <span>{{ $t('OrderCurrent.modal.odoo_invoices') }}: {{ odooInvoiceSummary.invoiceCount }}</span>
              <span>{{ $t('OrderCurrent.modal.odoo_orders') }}: {{ odooInvoiceSummary.orderCount }}</span>
              <span>{{ $t('OrderCurrent.modal.odoo_subtotal') }}: {{ formatMoney(odooInvoiceSummary.subtotal) }}</span>
              <span>{{ $t('OrderCurrent.modal.odoo_tax') }}: {{ formatMoney(odooInvoiceSummary.taxAmount) }}</span>
              <span>{{ $t('OrderCurrent.modal.odoo_total') }}: {{ formatMoney(odooInvoiceSummary.total) }}</span>
            </div>
          </template>

          <template #pre-body-content>
            <div class="m-2">
              <label for="spreadDate">{{$t('OrderCurrent.modal.billing.date_label')}}</label>
              <custom-date-pick
                :displayFormatPRP="'YYYY-MM-DD'"
                :formatPRP="'YYYY/MM/DD'"
                :pickTimePRP="false"
                :use12HourClockPRP="false"
                :hasInputElement="true"
                @updateDatePick="updateDatePick"
              />
              <button class="mt-1 btn btn-kne-orange" @click="applyBillingDateToAll(spreadDate)">{{$t('OrderCurrent.sameBillingDate')}}</button>
            </div>
          </template>

          <template #body-content-desktop="content">
            <!-- List of orders -->
            <tr v-for="(order, index) in content.results"
            class="table-element_hover text-center"
            :key="index"
            :class="{
              'drop': order.status === 'Drop',
              'cancel': order.status === 'Cancel',
              'waiting': order.status === 'Attente',
              'pick': order.status === 'Pick',
              'transit': order.status === 'Transit',
              'dispatch': order.status === 'Dispatch',
              'reject': order.status === 'Reject',
              'inspection': order.status === 'Inspection',
              'transfered': order.status === 'Transférée'
              }">
              <td class="pt-2">
                <span class="nowrap">
                  <hover-tool-tip
                  v-if="!order.historySync || !checkHistorySyncDrops(order.historySync)"
                  :text="`<div>
                            ${$t('OrderCurrent.modal.orderNotCompleted')}
                          </div>`"
                  :top="true"
                  :right="true"
                  :iconColor="'red'"
                  :iconClass="'fa-exclamation-triangle'"
                  clickable
                  />
                  {{ order.orderNumber }}
                  <div v-if="order.isOdooInvoiceGroup" class="grouped-orders-list">
                    {{ order.orderNumbers.join(', ') }}
                  </div>
                  <hover-tool-tip
                    :text="`<div>
                              ${order.source || '-'}
                              <br>${getDate(order.creationDate) || '-'}
                              <br>${order.make || '-'} ${order.model || '-'} ${order.year || '-'}
                            </div>`"
                    :top="true"
                    :right="true"
                    clickable
                  />
                  <hover-tool-tip
                    :text="generateTooltip(order)"
                    :right="true"
                    :top="true"
                    :iconColor="'green'"
                    :iconClass="'fa-receipt'"
                    clickable
                  />
                </span>
              </td>
              <td class="nowrap">
                <span>
                  {{shortenString($realName(order, 'payer'))}}
                  <hover-tool-tip
                  :text="`<div>
                            <p>
                              ${order.transmitterAddress || '-'} ${order.transmitterCity || '-'}, ${order.transmitterProvince || '-'} ${order.transmitterPostcode || '-'}, ${order.transmitterCountry || '-'}
                            </p>
                            ->
                            <p>
                              ${order.receiverAddress || '-'} ${order.receiverCity || '-'}, ${order.receiverProvince || '-'} ${order.receiverPostcode || '-'}, ${order.receiverCountry || '-'}
                            </p>
                          </div>`"
                  right
                  :top="true"
                  clickable
                  />
                </span>
              </td>
              <td>
                {{order.numRef || "No Reference"}}
              </td>
              <td>
                <regular-input
                min="0"
                :value="order.price"
                @input="order.price = $event"
                :disabled="order.isOdooInvoiceGroup"
                type="number" :placeholder="$t('OrderCurrent.price')"/>
              </td>
              <td>
                <regular-input
                  min="0"
                  :value="order.fuelCharge"
                  @input="updateFuelCharge(order, $event)"
                  type="number"
                  placeholder="Fuel Charge"
                />
              </td>
              <td>
                <regular-select
                :value="order.taxes"
                @change="updateInvoiceTaxes(order, $event)"
                :placeholder="$t('OrderInfo.modal.tax')">
                  <option v-for="province in $store.state.provinces" :value="province">{{province}}</option>
                </regular-select>
              </td>
              <td>
                <regular-input
                :value="order.taxesComment"
                @input="updateTaxesComment(order, $event)"
                type="text" :placeholder="$t('OrderCurrent.taxescomment')"/>
              </td>
              <td>
                <regular-input
                :value="order.billingDate"
                @input="updateBillingDate(order, formatDate($event))"
                type="date" :placeholder="$t('OrderCurrent.billingDate')"/>
              </td>
              <td v-if="isOdooBill">
                <span :class="getOdooStatusClass(order)">{{ getOdooStatusText(order) }}</span>
              </td>
            </tr>
          </template>

          <template #post-body-content>
            <div class="flex-row my-2" key="post-body-content">
              <div v-if="!isOdooBill">
                <button v-if="isQuickBook" @click="onSaveBillingOrder()" class="btn btn-kne-orange m-1">{{$t('General.save')}}</button>
                <button v-if="isQuickBook" @click="onFinishBilling()" class="btn btn-kne-orange m-1">{{$t('General.finish')}}</button>
              </div>
              <div v-if="isOdooBill"> 
                <button @click="onSaveBillingOrderOdoo()" class="btn btn-kne-orange m-1">{{$t('OrderCurrent.modal.saveToOdoo')}}</button>
                <button @click="refreshOdooStatuses()" class="btn btn-kne-orange m-1">{{ $t('OrderCurrent.modal.refreshOdooStatus') }}</button>
                <button :disabled="!allOdooInvoicesVerified" @click="markOdooOrdersAsDone()" class="btn btn-kne-orange m-1">{{ $t('OrderCurrent.modal.markOdooDone') }}</button>
              </div>
              <div>
                <custom-json-excel
                  v-if="isQuickBook && !isOdooBill"
                  class="btn btn-kne-orange float-right m-1"
                  :filterResults="quickbookExportData"
                  :customerFields="billingFields"
                  :name="'order_current_' + formatDate(Date.now()) + '.xls'"
                  />
                <a v-if="isSageOne && !isOdooBill" target="_blank" :href="'https://www.sageone.com/oauth2/auth/central?filter=apiv3.1&response_type=code&client_id=85d29ac7-0ebf-4504-a447-b672936b95d9/6f21ee52-cf07-45b5-8b64-fa93ed0a18e9&redirect_uri=https://systems.keepneye.com/api4/SageoneToken&scope=full_access&state=' + allOrderID ">
                  <button :disabled="!allBillingFieldCompleted" @click="saveBilling(); clearSelected(); clearSelected()" class="btn btn-kne-orange m-1 float-right">{{$t('OrderCurrent.modal.confirm')}}</button>
                </a>
                <button @click="clearSelected()" class="btn btn-danger float-right m-1">{{$t('OrderCurrent.modal.cancel')}}</button>
              </div>
            </div>
          </template>
        </table-element>
    </modal>
</template>

<script>
import Modal from '../../interface-components/Modal.vue'
import regularInput from '../../basic-components/regularInput.vue'
import regularSelect from '../../basic-components/regularSelect.vue'
import TableElement from '../Table-component/Table-Element.vue'
import CustomDatePick from '../Custom-packages/Custom-DatePick.vue'
import CustomJsonExcel from '../Custom-packages/Custom-JsonExcel.vue'
import FeeSelector from '../../basic-components/feeSelector.vue'
import hoverToolTip from '../../basic-components/hoverToolTip.vue'

import { db } from '../../../../firebase'
import { publishBillBatchSyncRequest } from '../../../../odoo-sync-requests/bill'
import * as odooBilling from '../../../../utils/odooBilling'

export default {
    name: 'BillingNew',
    components: {
        Modal,
        TableElement,
        FeeSelector,
        regularInput,
        regularSelect,
        hoverToolTip,
        CustomDatePick,
        CustomJsonExcel
    },

    data () {
        return {
            fieldsObjBIL: [
                {
                    text: this.$t('OrderCurrent.order'),
                    type: 'orderNumber',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.payer'),
                    type: 'payerName',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.reference'),
                    type: 'numRef',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.price'),
                    type: 'price',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.fuel_charge'),
                    type: 'fuelCharge',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.taxes'),
                    type: 'taxes',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.taxescomment'),
                    type: 'taxesComment',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.billingDate'),
                    type: 'billingDate',
                    value: ''
                },
                {
                    text: this.$t('OrderCurrent.modal.odoo_status'),
                    type: 'odooStatus',
                    value: ''
                }
            ],
            taxRates: { ...odooBilling.TAX_RATES },
            spreadDate: '',
            selectedFull: [],
            billingFields: {
                'Order': 'Order',
                'Ref': 'Ref',
                'Item': 'Item',
                'Date': 'Date',
                'Price': 'Price',
                'Description': 'Description',
                'Billing': 'Billing',
                'ToBeEmailed': 'ToBeEmailed',
                'Driver': 'Driver',
                'Tax': 'Tax'
            }
        }
    },

    props: {
        billing: Boolean,
        isOdooBill: {
          type: Boolean,
          required: false,
          default: false
        },
        arrOfOrder: {
          type: Array,
          required: true
        }
    },

    methods: {
        setNoteName (user) {
          const safeUser = user || {}
          const isFirstNameValid = (typeof safeUser.firstName !== 'undefined' && safeUser.firstName !== '')
          const isLastNameValid = (typeof safeUser.lastName !== 'undefined' && safeUser.lastName !== '')

          if (!isFirstNameValid || !isLastNameValid) return this.$store.state.userInfo.name
          return `${safeUser.firstName} ${safeUser.lastName}`
        },
        normalizePriceForNote (value) {
          return odooBilling.normalizePrice(value)
        },
        formatPriceForNote (value) {
          return `${this.normalizePriceForNote(value).toFixed(2)}$`
        },
        buildOdooBillingNoteText (price) {
          return `SENT TO ODOO: ${this.formatPriceForNote(price)}`
        },
        formatMoney (value) {
          const amount = Number(value) || 0
          return amount.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })
        },
        getOrderNumber (order) {
          return odooBilling.getOrderNumber(order)
        },
        getInvoiceOrders (invoice) {
          return odooBilling.getInvoiceOrders(invoice)
        },
        getDefaultTaxCode (order) {
          return odooBilling.getDefaultTaxCode(order)
        },
        getTaxRate (invoice) {
          return odooBilling.getTaxRate(invoice)
        },
        getOrderFeesTotal (order) {
          return odooBilling.getOrderFeesTotal(order)
        },
        getOrderFuelAmount (order) {
          return odooBilling.getOrderFuelAmount(order, this.defaultFuelCharge)
        },
        getOrderSubtotal (order) {
          return odooBilling.getOrderSubtotal(order, this.defaultFuelCharge)
        },
        getInvoiceSubtotal (invoice) {
          return odooBilling.getInvoiceSubtotal(invoice, this.defaultFuelCharge)
        },
        getInvoiceTaxAmount (invoice) {
          return odooBilling.getInvoiceTaxAmount(invoice, this.defaultFuelCharge)
        },
        getInvoiceTotal (invoice) {
          return odooBilling.getInvoiceTotal(invoice, this.defaultFuelCharge)
        },
        getOdooReference (order) {
          return odooBilling.getOdooReference(order)
        },
        getOdooStatusText (invoice) {
          const orders = this.getInvoiceOrders(invoice)
          const refs = orders.map(order => this.getOdooReference(order)).filter(Boolean)

          if (refs.length === orders.length) return refs.join(', ')
          if (refs.length > 0) return `${this.$t('OrderCurrent.modal.odoo_partial_sync')}: ${refs.join(', ')}`
          if (orders.some(order => order.odooSyncStatus === 'sync_requested')) return this.$t('OrderCurrent.modal.odoo_sync_requested')
          return this.$t('OrderCurrent.modal.odoo_not_synced')
        },
        getOdooStatusClass (invoice) {
          const orders = this.getInvoiceOrders(invoice)
          const refs = orders.map(order => this.getOdooReference(order)).filter(Boolean)

          if (refs.length === orders.length) return 'text-success'
          if (refs.length > 0 || orders.some(order => order.odooSyncStatus === 'sync_requested')) return 'text-warning'
          return 'text-danger'
        },
        prepareBillingOrder (order) {
          if (!order) return order

          if (this.isOdooBill && (!order.taxes || order.taxes === '')) {
            this.$set(order, 'taxes', this.getDefaultTaxCode(order))
          }
          if (order.taxesComment === undefined) {
            this.$set(order, 'taxesComment', '')
          }
          if (!order.billingDate) {
            this.$set(order, 'billingDate', this.formatDate(Date.now()))
          }

          return order
        },
        updateInvoiceTaxes (invoice, value) {
          this.$set(invoice, 'taxes', value)
          this.getInvoiceOrders(invoice).forEach(order => {
            this.$set(order, 'taxes', value)
          })
        },
        updateBillingDate (invoice, value) {
          this.$set(invoice, 'billingDate', value)
          this.getInvoiceOrders(invoice).forEach(order => {
            this.$set(order, 'billingDate', value)
          })
        },
        updateTaxesComment (invoice, value) {
          this.$set(invoice, 'taxesComment', value)
          this.getInvoiceOrders(invoice).forEach(order => {
            this.$set(order, 'taxesComment', value)
          })
        },
        applyBillingDateToAll (date) {
            this.billingRows.forEach((element) => {
                this.updateBillingDate(element, date)
            })
        },
        checkHistorySyncDrops (historysync) {
            let dropFlag = true
            for (const key in historysync) {
                if (!historysync[key].dropDate) {
                dropFlag = false
                break
                }
            }
            return dropFlag
        },
        getDate: function (timestamp) {
            var date = new Date(timestamp)
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
        },
        shortenString (text) {
          if (typeof text === 'undefined') return text
          else {
            let subString = text.substring(0, 22)
            if (text.length > 22) subString += '...'
            return subString
          }
        },
        updateFuelCharge (order, updatedCharge) {
          this.$set(order, 'fuelCharge', updatedCharge)
          if (updatedCharge !== '') {
            this.$set(order, 'includedFuelCharge', false)
            this.$set(order, 'applyFuelCharge', true)
          }
          this.getInvoiceOrders(order).forEach(invoiceOrder => {
            this.$set(invoiceOrder, 'fuelCharge', updatedCharge)
            if (updatedCharge !== '') {
              this.$set(invoiceOrder, 'includedFuelCharge', false)
              this.$set(invoiceOrder, 'applyFuelCharge', true)
            }
          })
        },
        getBillingUpdateFields (element) {
          const updateFields = {
            'price': element.price,
            'taxes': element.taxes,
            'fuelCharge': element.fuelCharge,
            'taxesComment': element.taxesComment,
            'billingDate': element.billingDate
          }

          if (element.includedFuelCharge !== undefined) {
            updateFields.includedFuelCharge = element.includedFuelCharge
          }
          if (element.applyFuelCharge !== undefined) {
            updateFields.applyFuelCharge = element.applyFuelCharge
          }

          return updateFields
        },
        getFuelChargePercent (order) {
          return odooBilling.getFuelChargePercent(order, this.defaultFuelCharge)
        },
        generateTooltip (order) {
            if (!order) return '-'

            let htmlParts = []

            if (Array.isArray(order.fees) && order.fees.length > 0) {
                order.fees.forEach(fee => {
                if (fee.amount != null && fee.text) {
                    htmlParts.push(`${fee.text}: ${fee.amount}$`)
                }
                })
            }

            if (htmlParts.length === 0) return '-'

            return `<p>${htmlParts.join('<br>')}</p>`
        },
        saveBilling () {
          this.selectedFull.forEach((element, index) => {
            db.ref('database').child('transporter').child(this.companyUID).child('deliveries').child(element.orderNumber).update(this.getBillingUpdateFields(element)).then(() => {
              // ======================== ADD FAST BILLING LOG =======================
              this.$store.dispatch('newLog', {'UID': this.uid, 'action': 'FAST_BILLING', 'orderID': element.orderNumber, 'date': Date.now()})
              // ======================== ADD FAST BILLING LOG =======================
            })
          })

          this.clearSelected()
        },

        async onSaveBillingOrder () {
          await this.selectedFull.forEach((element, index) => {
            db.ref('database')
            .child('transporter')
            .child(this.companyUID)
            .child('deliveries')
            .child(element.orderNumber)
            .update(this.getBillingUpdateFields(element)).then(() => {
              // ======================== ADD FAST BILLING LOG =======================
              this.$store.dispatch('newLog', {'UID': this.uid, 'action': 'FAST_BILLING', 'orderID': element.orderNumber, 'date': Date.now()})
              // ======================== ADD FAST BILLING LOG =======================
            })
          })

          this.openBilling()
          this.$emit('closeBillingModal')
        },

        buildOrderOdooLines (order) {
          return odooBilling.buildOrderOdooLines(order, this.defaultFuelCharge)
        },
        buildOdooInvoicePayload (invoice, batchId) {
          return odooBilling.buildOdooInvoicePayload(invoice, batchId, this.defaultFuelCharge)
        },

        async onSaveBillingOrderOdoo () {
          try {
            const batchId = `${this.companyUID}-${Date.now()}`
            const invoices = this.odooInvoiceRows.map(invoice => this.buildOdooInvoicePayload(invoice, batchId))

            for (const invoice of this.odooInvoiceRows) {
              const orders = this.getInvoiceOrders(invoice)
              for (const element of orders) {
              const orderId = this.getOrderNumber(element)
              const orderPath = db.ref('database')
                .child('transporter')
                .child(this.companyUID)
                .child('deliveries')
                .child(orderId)
              const notePath = orderPath.child('notes')

              await orderPath.update({
                ...this.getBillingUpdateFields(element),
                taxes: invoice.taxes,
                taxesComment: invoice.taxesComment || element.taxesComment || '',
                billingDate: invoice.billingDate,
                odooSyncStatus: 'sync_requested',
                odooSyncRequestedAt: Date.now(),
                odooInvoiceBatchId: batchId,
                odooInvoiceGroupId: invoice.odooInvoiceGroupId || element.odooInvoiceGroupId || ''
              })

              await notePath.push({
                date: Date.now(),
                name: this.setNoteName(this.userInfo),
                text: this.buildOdooBillingNoteText(this.getOrderSubtotal(element))
              })

              // ======================== ADD FAST BILLING LOG =======================
              this.$store.dispatch('newLog', {'UID': this.uid, 'action': 'FAST_BILLING', 'orderID': orderId, 'date': Date.now()})
              // ======================== ADD FAST BILLING LOG =======================
              }
            }

            await publishBillBatchSyncRequest(invoices, this.companyUID, batchId)
            this.clearSelected()
          } catch (error) {
            console.error('Error while saving Odoo billing order(s):', error)
          }
        },

        onFinishBilling () {
          this.selectedFull.forEach(async (element, index) => {
            let orderId = element.orderNumber

            await db.ref('database').child('transporter').child(this.companyUID).child('deliveries').child(orderId).update({'status': 'Terminée'}).then(() => {
              // ======================== ADD FAST BILLING LOG =======================
              this.$store.dispatch('newLog', {'UID': this.uid, 'action': 'FAST_BILLING', 'orderID': orderId, 'date': Date.now()})
              // ======================== ADD FAST BILLING LOG =======================
            })
          })

          this.clearSelected()
        },
        async refreshOdooStatuses () {
          const orderIds = this.selectedFull.map(order => this.getOrderNumber(order)).filter(Boolean)
          for (const orderId of orderIds) {
            const snapshot = await db.ref('database')
              .child('transporter')
              .child(this.companyUID)
              .child('deliveries')
              .child(orderId)
              .once('value')

            if (snapshot.exists()) {
              const updatedOrder = snapshot.val()
              const existingOrder = this.selectedFull.find(order => this.getOrderNumber(order) === orderId)
              if (existingOrder) {
                Object.keys(updatedOrder).forEach(key => {
                  this.$set(existingOrder, key, updatedOrder[key])
                })
                this.$set(existingOrder, 'orderNumber', orderId)
              }
            }
          }
        },
        async markOdooOrdersAsDone () {
          if (!this.allOdooInvoicesVerified) return

          for (const order of this.selectedFull) {
            const orderId = this.getOrderNumber(order)
            await db.ref('database')
              .child('transporter')
              .child(this.companyUID)
              .child('deliveries')
              .child(orderId)
              .update({
                isBilling: false,
                status: 'Terminée',
                odooBillingCompletedAt: Date.now()
              })

            await this.$store.dispatch('newLog', {'UID': this.uid, 'action': 'FAST_BILLING', 'orderID': orderId, 'date': Date.now()})
          }

          this.clearSelected()
        },
        clearSelected () {
          this.$emit('closeBillingModal')
          this.selectedFull = []
        },
        updateDatePick (date) {
            date = this.formatDate(date)
            this.spreadDate = date
        },
        formatDate (date) {
          let fdate = new Date(date)
          fdate = fdate.getFullYear() + '-' + ('0' + (fdate.getMonth() + 1)).slice(-2) + '-' + ('0' + fdate.getUTCDate()).slice(-2)

          return fdate
        }
    },

    computed: {
      billingFieldsObj () {
        if (this.isOdooBill) return this.fieldsObjBIL
        return this.fieldsObjBIL.filter(field => field.type !== 'odooStatus')
      },
      billingRows () {
        return this.isOdooBill ? this.odooInvoiceRows : this.selectedFull
      },
      odooInvoiceRows () {
        const rows = []
        const groupMap = {}
        const groupOrder = []

        this.selectedFull.forEach(order => {
          this.prepareBillingOrder(order)
          if (order.odooInvoiceGroupEnabled && order.odooInvoiceGroupId) {
            if (!groupMap[order.odooInvoiceGroupId]) {
              groupMap[order.odooInvoiceGroupId] = []
              groupOrder.push(order.odooInvoiceGroupId)
            }
            groupMap[order.odooInvoiceGroupId].push(order)
          } else {
            rows.push(order)
          }
        })

        groupOrder.forEach(groupId => {
          const orders = groupMap[groupId]
          if (orders.length === 1) {
            rows.push(orders[0])
            return
          }

          const firstOrder = orders[0]
          const price = orders.reduce((total, order) => total + (Number(order.price) || 0), 0)
          const groupRow = {
            ...firstOrder,
            invoiceKey: groupId,
            orderNumber: `${this.$t('OrderCurrent.modal.odoo_group')} (${orders.length})`,
            orderNumbers: orders.map(order => this.getOrderNumber(order)),
            isOdooInvoiceGroup: true,
            orders,
            price: price.toFixed(2),
            regularPrice: orders.reduce((total, order) => total + (Number(order.regularPrice) || 0), 0).toFixed(2),
            vin: `${orders.length} orders`,
            model: `${orders.length} orders`,
            odooInvoiceGroupId: groupId,
            taxes: firstOrder.taxes || this.getDefaultTaxCode(firstOrder),
            taxesComment: firstOrder.taxesComment || '',
            billingDate: firstOrder.billingDate || this.formatDate(Date.now())
          }

          rows.push(groupRow)
        })

        return rows
      },
      odooInvoiceSummary () {
        const summary = {
          invoiceCount: this.odooInvoiceRows.length,
          orderCount: this.selectedFull.length,
          subtotal: 0,
          taxAmount: 0,
          total: 0
        }

        this.odooInvoiceRows.forEach(invoice => {
          summary.subtotal += this.getInvoiceSubtotal(invoice)
          summary.taxAmount += this.getInvoiceTaxAmount(invoice)
          summary.total += this.getInvoiceTotal(invoice)
        })

        return summary
      },
      allOdooInvoicesVerified () {
        return this.odooInvoiceRows.length > 0 && this.odooInvoiceRows.every(invoice => {
          return this.getInvoiceOrders(invoice).every(order => !!this.getOdooReference(order))
        })
      },
      companyUID () {
        return this.$store.state.authorization.company
      },
      uid () {
        return this.$store.state.user.uid
      },
      userInfo () {
        return this.$store.state.userInfo
      },
      defaultFuelCharge () {
        return this.$store.state.userInfo.fuelCharge || 0
      },
      isSageOne () {
        return this.$store.state.userInfo.isSageOne
      },
      isQuickBook () {
        return this.$store.state.userInfo.isQuickbook
      },
      quickbookExportData () {
        let exportData = []
        this.selectedFull.forEach((order) => {
          const fuelChargePercent = this.getFuelChargePercent(order)
          const fuelChargeAmount = (order.includedFuelCharge || order.applyFuelCharge === false) ? 0 : (Number(order.price) * Number(fuelChargePercent)) / 100

          const salesRow = {
            'Order': 'QB' + order.orderNumber,
            'Ref': order.numRef || '',
            'Item': 'Sales',
            'Date': order.billingDate || '',
            'Price': order.price || 0,
            'Description': (order.make || '') + ' ' + (order.model || '') + ' ' + (order.year || '') + ' - VIN: ' + (order.vin || '') + ' ' + (this.$realName(order, 'transmitter') || '') + ' to ' + (this.$realName(order, 'receiver') || '') + ' Shipped to : ' + (order.receiverProvince || ''),
            'Billing': this.$realName(order, 'payer') || '',
            'ToBeEmailed': true,
            'Driver': order.driverName || '',
            'Tax': 'H'
          }

          const fuelRow = {
            'Order': 'QB' + order.orderNumber,
            'Ref': order.numRef || '',
            'Item': 'Fuel Surcharge',
            'Date': order.billingDate || '',
            'Price': fuelChargeAmount,
            'Description': fuelChargePercent + '%',
            'Billing': this.$realName(order, 'payer') || '',
            'ToBeEmailed': true,
            'Driver': ' ',
            'Tax': 'H'
          }

          exportData.push(fuelRow)
          exportData.push(salesRow)
        })
        return exportData.reverse()
      }
    },

    watch: {
      arrOfOrder: {
        immediate: true,
        handler (val) {
          this.selectedFull = Array.isArray(val) ? val.map(order => this.prepareBillingOrder(order)) : []
        }
      }
    }
}
</script>

<style scoped>
.billing-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-weight: 600;
}

.grouped-orders-list {
  font-size: 12px;
  white-space: normal;
  max-width: 260px;
}
</style>
